import { FGButton, FGIcons, FGStack, FGToastDismiss, FGTypography } from '@fg/design-system';
import EventsDisplayProvider from 'components/eventsFeed/EventDisplayProvider';
import EventFeedDataProvider from 'components/eventsFeed/EventFeedDataProvider';
import { GQTabsNew } from 'components/GQTabs/GQTabs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useNavigate,
    useMatch,
    Navigate,
    Route,
    Routes,
} from 'react-router-dom';
import rootStore, { IMobxRootState } from '../../../RootStore';
import { INSIGHT_FEED_TYPE, VIEW } from 'interfaces';
import { inject } from 'mobx-react';
import { isEmpty } from 'lodash/fp';
import InsightDisplayProvider from './InsightDisplayProvider';
import PredictionDisplayProvider from './PredictionDisplayProvider';
import InsightsProvider from './InsightsProvider';
import PredictionsProvider from './PredictionsProvider';
import useIsGQInternalUser from '../../../hooks/useIsGQInternalUser';
import { IActivityRecord } from 'services/APIService';
import { exportPrediction } from 'components/GQTemplateV2/exportUtils';
import { createExportErrorToaster, createExportStartToaster } from 'components/GQChart/ExportChartToaster';

const TAB_ROUTE_MAP: Record<string, string> = {
    events: 'events',
    insights: 'insights',
    predictions: 'predictions',
    my_insights: 'my_insights',
};
const ROUTE_TAB_MAP: Record<string, string> = {
    events: 'events',
    insights: 'insights',
    predictions: 'predictions',
    my_insights: 'my_insights',
};
const BASE_TABS = [
    {
        id: 'events',
        label: 'Events',
        header: 'Analysis & Predictions',
    },
    {
        id: 'insights',
        label: 'Insights',
        header: 'Analysis & Predictions',
    },
    {
        id: 'predictions',
        label: 'Predictions',
        header: 'Analysis & Predictions',
    },
];
const MY_INSIGHTS_TAB = {
    id: 'my_insights',
    label: 'My Insights',
    header: 'Analysis & Predictions',
};
const NO_DATA_TO_EXPORT_TOOLTIP = 'No data to export';
type Props = {
    maxDate?: moment.Moment;
    defaultEvent?: number;
    eventIds?: string[];
    insightIds?: string[];
    myInsightIds?: string[];
    predictionIds?: string[];
    eventSearchString?: string;
};
//todo: the name 'Risk Intelligence' is incorrect.
//this component is a navigation and tabs container for events, insights & predictions
export const RiskIntelligence = inject(
    ({ eventsFeedStore, insightsFeedStore, predictionsFeedStore }: IMobxRootState) => {
        return {
            maxDate: eventsFeedStore.maxDate,
            defaultEvent: eventsFeedStore.defaultEvent,
            eventIds: eventsFeedStore.currentEventIds,
            insightIds: insightsFeedStore.currentIds,
            myInsightIds: insightsFeedStore.myCurrentIds,
            predictionIds: predictionsFeedStore.currentIds,
            eventSearchString: eventsFeedStore.searchString,
        };
    }
)(({
    maxDate,
    defaultEvent,
    eventIds,
    insightIds,
    myInsightIds,
    predictionIds,
    eventSearchString,
}: Props) => {
    const navigate = useNavigate();
    const isGQInternalUser = useIsGQInternalUser();
    // Seed all three feed filters with the selected countries on entry; user can still edit.
    useEffect(() => {
        const selectedCountries = rootStore.countryStore.currentCountryList;
        if (!isEmpty(selectedCountries)) {
            rootStore.eventsFeedStore.setSelectedCountries(selectedCountries);
            rootStore.insightsFeedStore.setSelectedCountries(selectedCountries);
            rootStore.predictionsFeedStore.setSelectedCountries(
                selectedCountries
            );
        }
    }, []);
    const tabs = useMemo(
        () => (isGQInternalUser ? [...BASE_TABS, MY_INSIGHTS_TAB] : BASE_TABS),
        [isGQInternalUser]
    );
    const match = useMatch(`/${VIEW.RISK_INTELLIGENCE}/:insightsView/:id?`);
    const { tab, id } = useMemo(() => {
        const routeSegment = match?.params.insightsView;
        const resolvedTab = ROUTE_TAB_MAP[routeSegment];
        return { tab: resolvedTab, id: match?.params.id };
    }, [match]);
    const handleMyInsightDetected = useCallback(
        (insight: IActivityRecord | null) => {
            setActivePdfUrl(insight?.pdfURL);
            if (
                insight?.createdByMe &&
                isGQInternalUser &&
                match?.params.insightsView === 'insights' &&
                match?.params.id
            ) {
                navigate(
                    `/${VIEW.RISK_INTELLIGENCE}/my_insights/${match.params.id}`,
                    { replace: true }
                );
            }
        },
        [isGQInternalUser, match, navigate]
    );
    const [activePdfUrl, setActivePdfUrl] = useState<string | undefined>();
    const [isExportingPrediction, setIsExportingPrediction] = useState(false);
    const handleExportPrediction = useCallback(async () => {
        setIsExportingPrediction(true);
        const toastId = createExportStartToaster();
        try {
            await exportPrediction(id);
        } catch (e) {
            createExportErrorToaster();
            console.error(e);
        } finally {
            FGToastDismiss(toastId);
            setIsExportingPrediction(false);
        }
    }, [id]);
    const handleMyInsightLoaded = useCallback(
        (insight: IActivityRecord | null) => {
            setActivePdfUrl(insight?.pdfURL);
        },
        []
    );
    const [prevListId, nextListId] = useMemo(() => {
        const tabToIds: Record<string, string[] | undefined> = {
            events: eventIds,
            insights: insightIds,
            my_insights: myInsightIds,
            predictions: predictionIds,
        };
        const list = tabToIds[tab];
        const effectiveId =
            id ||
            (tab === 'events' && defaultEvent
                ? String(defaultEvent)
                : undefined);
        if (!list || !effectiveId) return [undefined, undefined];
        const idx = list.indexOf(effectiveId);
        if (idx < 0) return [undefined, undefined];
        return [
            idx > 0 ? list[idx - 1] : undefined,
            idx < list.length - 1 ? list[idx + 1] : undefined,
        ];
    }, [tab, id, defaultEvent, eventIds, insightIds, myInsightIds, predictionIds]);
    const isSearchingEvents = !isEmpty(eventSearchString);
    const ownFeedRef = useRef({ hasLoaded: false, ids: [] as string[] });
    useEffect(() => {
        if (tab !== 'events') {
            ownFeedRef.current.hasLoaded = false;
            return;
        }
        if (isEmpty(eventIds)) {
            ownFeedRef.current.hasLoaded = true;
            return;
        }
        if (!ownFeedRef.current.hasLoaded) return;
        if (
            id &&
            !isSearchingEvents &&
            ownFeedRef.current.ids.includes(id) &&
            !eventIds.includes(id)
        ) {
            navigate(`/${VIEW.RISK_INTELLIGENCE}/${TAB_ROUTE_MAP[tab]}`, { replace: true });
        }
        ownFeedRef.current.ids = eventIds;
    }, [tab, id, eventIds, navigate, isSearchingEvents]);
    const hasNoInsightsToExport = isEmpty(tab === 'my_insights' ? myInsightIds : insightIds);
    const hasNoPredictionsToExport = isEmpty(predictionIds);
    const handleTabChange = useCallback(
        (newTab: string) => {
            const routeSegment = TAB_ROUTE_MAP[newTab] || 'events';
            navigate(`/${VIEW.RISK_INTELLIGENCE}/${routeSegment}`);
        },
        [navigate]
    );
    return (
        <FGStack
            data-testid="analysis-predictions"
            style={{
                width: '100%',
                height: '100%',
            }}
            gap={0}>
            <FGStack direction="column" gap={0} style={{ flex: 1 }}>
                <FGStack
                    gap={0}
                    direction="column"
                    style={{ padding: '16px 16px 0' }}>
                    <FGStack
                        justifyContent="space-between"
                        style={{ marginBottom: 12 }}>
                        <FGTypography
                            variant="headline4"
                            tag="h4"
                            color="strong">
                            {tabs.find(t => t.id === tab)?.header ||
                                'Analysis & Predictions'}
                        </FGTypography>
                        <FGStack gap={4} alignItems="center">
                            <FGButton
                                data-testid="analysis-previous"
                                disabled={!prevListId}
                                onClick={() =>
                                    prevListId &&
                                    navigate(
                                        `/${VIEW.RISK_INTELLIGENCE}/${TAB_ROUTE_MAP[tab]}/${prevListId}`
                                    )
                                }
                                variant="secondary"
                                icon={<FGIcons icon="ArrowUp" />}
                                appearance="ghost"
                            />
                            <FGButton
                                data-testid="analysis-next"
                                disabled={!nextListId}
                                onClick={() =>
                                    nextListId &&
                                    navigate(
                                        `/${VIEW.RISK_INTELLIGENCE}/${TAB_ROUTE_MAP[tab]}/${nextListId}`
                                    )
                                }
                                variant="secondary"
                                icon={<FGIcons icon="ArrowDown" />}
                                appearance="ghost"
                            />
                            {['insights', 'my_insights'].includes(tab) && (
                                <FGButton
                                    data-testid="analysis-export-insight"
                                    variant="secondary"
                                    appearance="ghost"
                                    icon={
                                        <FGIcons
                                            icon="Download"
                                            color="#fff"
                                        />
                                    }
                                    disabled={!activePdfUrl || hasNoInsightsToExport}
                                    title={hasNoInsightsToExport && NO_DATA_TO_EXPORT_TOOLTIP}
                                    onClick={() =>
                                        activePdfUrl &&
                                        window.open(activePdfUrl, '_blank')
                                    }>
                                    <FGTypography
                                        variant="button2"
                                        tag="span"
                                        color="strong">
                                        Export Insight
                                    </FGTypography>
                                </FGButton>
                            )}
                            { tab === 'predictions' && (
                                <FGButton
                                    data-testid="analysis-export-prediction"
                                    variant="secondary"
                                    icon={
                                        <FGIcons
                                            icon="Download"
                                            color="#fff"
                                        />
                                    }
                                    disabled={isExportingPrediction || hasNoPredictionsToExport}
                                    title={hasNoPredictionsToExport && NO_DATA_TO_EXPORT_TOOLTIP}
                                    onClick={handleExportPrediction}>
                                    <FGTypography
                                        variant="button2"
                                        tag="span"
                                        color="strong">
                                        Export Prediction
                                    </FGTypography>
                                </FGButton>
                            )}
                        </FGStack>
                    </FGStack>
                    <div style={{ width: 360 }}>
                        <GQTabsNew
                            tabs={tabs}
                            onChange={handleTabChange}
                            selectedTab={tab}
                        />
                    </div>
                    <div
                        style={{
                            width: '100%',
                            borderBottom:
                                '1px solid var(--fg-color-border-light)',
                        }}
                    />
                </FGStack>
                <FGStack
                    justifyContent="center"
                    style={{ flex: 1, overflowY: 'auto' }}>
                    <Routes>
                        <Route
                            path="events/:id?"
                            element={
                                <EventsDisplayProvider
                                    id={
                                        id ||
                                        (defaultEvent
                                            ? String(defaultEvent)
                                            : undefined)
                                    }
                                />
                            }
                        />
                        <Route
                            path="insights/:id?"
                            element={
                                <InsightDisplayProvider
                                    id={id}
                                    onInsightLoaded={handleMyInsightDetected}
                                />
                            }
                        />
                        <Route
                            path="my_insights/:id?"
                            element={
                                <InsightDisplayProvider
                                    id={id}
                                    onInsightLoaded={handleMyInsightLoaded}
                                />
                            }
                        />
                        <Route
                            path="predictions/:id?"
                            element={<PredictionDisplayProvider id={id} />}
                        />
                        <Route
                            path="*"
                            element={<Navigate to="events" replace />}
                        />
                    </Routes>
                </FGStack>
            </FGStack>
            {tab === 'events' && (
                <EventFeedDataProvider virtualToday={maxDate} />
            )}
            {tab === 'insights' && <InsightsProvider />}
            {tab === 'predictions' && <PredictionsProvider />}
            {tab === 'my_insights' && isGQInternalUser && (
                <InsightsProvider feedType={INSIGHT_FEED_TYPE.My} />
            )}
        </FGStack>
    );
});

export default RiskIntelligence;
